import React, {useCallback, useEffect, useState} from "react";
import cloneDeep from "lodash/cloneDeep";
import PropTypes from "prop-types";
import {ActivityCell, ActivityWrapper, TPSection} from "./styles";
import {formatDate} from "../../../../../../../utils/util";
import Badge from "react-bootstrap/Badge";

const componentName = "recommendedActivities";

const PREVENTION_AND_SCREENINGS_SUBTYPE_ORDER = {
  Biometrics: 0,
  Screenings: 1,
  Immunizations: 2,
  Other: 3,
};

const getOrderedSubTypes = (subTypes, name) => {
  if (name !== "Prevention and Screenings") {
    return subTypes;
  }

  return [...(subTypes || [])].sort((a, b) => {
    const aOrder = PREVENTION_AND_SCREENINGS_SUBTYPE_ORDER[a?.name] ?? Number.MAX_SAFE_INTEGER;
    const bOrder = PREVENTION_AND_SCREENINGS_SUBTYPE_ORDER[b?.name] ?? Number.MAX_SAFE_INTEGER;

    return aOrder - bOrder;
  });
};

const VisitActivitySubType = ({
  visitActivityType,
  subTypes,
  extraData,
  name,
  missedOpportunities,
}) => {
  return getOrderedSubTypes(subTypes, name)?.map((x, idx) => {
    const activities = x?.visitActivities?.filter((e) => e?.disabled === false);

    return activities.length > 0 ? (
      <div className="mt-2" key={`${componentName}-activitySubType-${x?.name}-${idx}`} id={`${componentName}-activitySubType-${x?.name}-${idx}`}>
        <div className="d-flex mb-2 align-items-center">
          {x?.name !== "None" && (
            <p id={`${componentName}-activitySubTypeTitle-${x?.name}-${idx}`} className="fs-6 mb-0">{x?.name}</p>
          )}
        </div>
        <TPSection visitActivityType={visitActivityType}>
          <VisitActivity
            visitActivityType={visitActivityType}
            subType={x?.name}
            extraData={extraData}
            activity={activities}
            name={name}
            missedOpportunities={missedOpportunities}
          />
          {name === "Needed Referrals" && (
            <ActivityWrapper visitActivityType={visitActivityType}>
              {visitActivityType?.name === "Needed Referrals" && (
                <div key={`${componentName}-visitActivity-referToSDOH`} id={`${componentName}-visitActivity-referToSDOH`}>
                  <div className="d-flex align-items-start">
                    <input id={`${componentName}-visitActivity-referToSDOHCheck`} className="form-check-input opacity-100 me-3" type="checkbox" value="" disabled />
                    <div className="d-flex justify-content-between w-100">
                      <div className="w-100">
                        <ActivityCell visitActivityType={visitActivityType}>
                          <label id={`${componentName}-visitActivity-referToSDOHLabel`} className="form-check-label" htmlFor={`${componentName}-visitActivity-referToSDOHCheck`}>
                            <span className="fw-bold me-1">Refer for Social Determinants of Health Need</span>
                          </label>
                        </ActivityCell>
                      </div>
                      <div className="d-flex flex-wrap w-100">
                        {["Yes", "No"].map((e) => (
                          <label
                            key={`${componentName}-visitActivity-referToSDOH${e}Value`}
                            id={`${componentName}-visitActivity-referToSDOH${e}Value`}
                            className="d-flex form-check-label me-3"
                            htmlFor={`${componentName}-visitActivity-referToSDOH${e}Check`}
                          >
                            <input id={`${componentName}-visitActivity-referToSDOH${e}Check`}
                              className="form-check-input me-2"
                              type="checkbox"
                              value=""
                              checked={false}
                              readOnly
                            />
                            <div id={`${componentName}-visitActivity-referToSDOH${e}Label`} className="nowrap">{e}</div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </ActivityWrapper>
          )}
        </TPSection>
      </div>
    ) : (
      null
    );
  });
};

const VisitActivity = ({
  visitActivityType,
  activity,
  subType,
  extraData,
  name,
  missedOpportunities,
}) => {
  const [sortedActivity, setSortedActivity] = useState([]);

  useEffect(() => {
    if (activity) {
      let retActivity = activity.filter((e) => e?.displayName && e?.currentStatus);
      retActivity = retActivity.map((m) => {
        if (m.validation && m.validation === "REJECTED" && m?.currentStatus?.toUpperCase() === "OPEN") {
          m.conditionType = "REJECTED";
        } else if (!m.conditionType) {
          m.conditionType = "SUSPECTED";
        }
        return m;
      });

      retActivity.sort(sortAlgo);
      setSortedActivity(retActivity);
    }
  }, [activity]);

  const sortAlgo = (a, b) => {
    const orderSeq = ["REJECTED", "RECAPTURED", "SUSPECTED"];
    return orderSeq.indexOf(a.conditionType) - orderSeq.indexOf(b.conditionType);
  };

  const getConditionTypeLabel = useCallback((condition) => {
    let lbl = "Suspected";
    if (condition) {
      if (condition.validation && condition.validation === "REJECTED") {
        lbl = "Rejected";
      } else if (condition.conditionType === "RECAPTURED") {
        lbl = "Recaptured";
      }
    }
    return (
      <small id={`${componentName}-visitActivity-${lbl}Conditions`} className="fst-italic text-black-50">
        <span className="fw-bold">{lbl}</span> Condition
      </small>
    );
  }, []);

  const isMissedOpportunity = (activityId) => {
    return missedOpportunities.some((opportunity) => opportunity.id === activityId);
  };

  return sortedActivity?.map((y, idx) => (
    <ActivityWrapper key={`${componentName}-visitActivity-${y.displayName}-${idx}`} visitActivityType={visitActivityType}>
      <div id={`${componentName}-visitActivity-${y.displayName}-${idx}`}>
        <div className="d-flex align-items-start">
          {subType !== "Immunizations" && (
            <input id={`${componentName}-visitActivity-${y.displayName}-Check-${idx}`} className="form-check-input opacity-100 me-3" type="checkbox" value="" disabled />
          )}
          <div className="d-flex justify-content-between w-100">
            <div className="w-100">
              <ActivityCell visitActivityType={visitActivityType}>
                <label className="form-check-label d-flex justify-content-between" htmlFor={`${componentName}-visitActivity-${y.displayName}-Check-${idx}`}>
                  <div>
                    {visitActivityType?.name === "Treatment Planning" && (
                      <i id={`${componentName}-visitActivity-TreatmentPlanning-${idx}`} className="far fa-info-circle mt-1 me-2"></i>
                    )}
                    {y?.validation === "REJECTED" &&  y?.currentStatus?.toUpperCase() === "OPEN" && (
                      <i id={`${componentName}-visitActivity-Rejected-${idx}`} className="fa-regular fa-circle-exclamation text-danger me-1"></i>
                    )}
                    {isMissedOpportunity(y.id) && (
                      <i id={`${componentName}-visitActivity-MissedOpportunity-${idx}`} className="fa-regular fa-hexagon-exclamation text-danger me-1"></i>
                    )}
                    <span id={`${componentName}-visitActivity-Name-${idx}`} className="fw-bold me-1">
                      {y?.displayName}
                    </span>
                    {name === "Documentation And Coding" && y?.actionType !== "SUSPECT" && (
                      <span id={`${componentName}-visitActivity-AbbrGapYear-${idx}`} className="fw-bold me-1">
                        {y?.abbreviation ? "(" + y?.abbreviation + ")" : ""}
                        {" " + y.gapAndYear}
                      </span>
                    )}
                  </div>
                  {subType === "Immunizations" && (
                    <span id={`${componentName}-visitProgressText-${idx}`} className="fw-bold">
                      {y?.immunizationsDTO?.completionPercent}% Complete
                    </span>
                  )}
                </label>
              </ActivityCell>
              {name === "Documentation And Coding" && getConditionTypeLabel(y)}
              {name === "Medication Review" && (
                <>
                  {extraData?.medicationCareGapValues?.find((f) => f?.measure === y?.displayName)?.drugName && (
                    <>
                      <small className="d-block text-black-50">
                        <span id={`${componentName}-MedicationReview-DrugName-${idx}`} className="fw-bold">
                          {
                            extraData?.medicationCareGapValues?.find((f) => f?.measure === y?.displayName)
                              ?.drugName
                          }
                        </span>{" "}
                        {extraData?.medicationCareGapValues?.find((f) => f?.measure === y?.displayName)?.dosage},
                        <span id={`${componentName}-MedicationReview-DaysSupply-${idx}`} className="fw-bold text-black-50">
                          {" "}
                          {
                            extraData?.medicationCareGapValues?.find((f) => f?.measure === y?.displayName)
                              ?.daysSupply
                          }{" "}
                          Day Supply
                        </span>
                      </small>
                      <small id={`${componentName}-MedicationReview-LastFillDate-${idx}`} className="d-block textb-lack-50">
                        Last Fill Date:{" "}
                        <span className="fw-bold">
                          {formatDate(
                            extraData?.medicationCareGapValues?.find((f) => f?.measure === y?.displayName)
                              ?.fillDate
                          )}
                        </span>
                      </small>
                      <small id={`${componentName}-MedicationReview-NextFillDate-${idx}`} className="d-block fw-bold text-black-50">
                        Next Expected Fill Date:{" "}
                        {formatDate(
                          extraData?.medicationCareGapValues?.find((f) => f?.measure === y?.displayName)
                            ?.nextFillDate
                        )}
                      </small>
                    </>
                  )}
                </>
              )}
              {name === "Prevention and Screenings" && subType === "Immunizations" && extraData?.immunDoseInfo && (
                <div>
                  {extraData?.immunDoseInfo?.map((x, idx) => (
                    <div key={`${componentName}-immunizationItem-${idx}`}
                      id={`${componentName}-immunizationItem-${idx}`}
                      className="d-flex justify-content-between align-items-start"
                    >
                      <div>
                        <input id={`${componentName}-immunizationItemCheck-${idx}`} className="form-check-input me-2" type="checkbox" disabled={true} />
                        <span id={`${componentName}-immunizationItemLabel-${idx}`} className="text-black-100">
                          {x?.immunizationDescription ? x?.immunizationDescription : ""}{" "}
                          {x?.immunizationAbbreviation ? `${x?.immunizationAbbreviation}` : ""}
                        </span>
                      </div>
                      <Badge id={`${componentName}-immunizationItemDoses-${idx}`} className={"badge bg-light rounded-pill text-dark border-0 text-dark"}>
                        {x?.doesActual}/{x?.totalDoses} Doses
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              {name === "Prevention and Screenings" && subType !== "Immunizations" && (
                <small id={`${componentName}-PrevScreenCodes-${idx}`}>{extraData?.careGapValues?.find((f) => f?.measure === y?.displayName)?.codes}</small>
              )}
              {name === "Needed Referrals" && (
                <small id={`${componentName}-NeedReferralsCodes-${idx}`}>{extraData?.careGapValues?.find((f) => f?.measure === y?.displayName)?.codes}</small>
              )}
            </div>
            {name === "Documentation And Coding" && (
              <div id={`${componentName}-DocCodingStatus-${idx}`} className="d-flex flex-wrap w-100">
                {["Confirmed", "Pending", "Does Not Have"].map((e, idx) => (
                  <label
                    key={`${componentName}-DocCodingStatusCheck-${idx}`}
                    id={`${componentName}-DocCodingStatusCheck-${idx}`}
                    className="d-flex form-check-label me-3"
                    htmlFor={`${componentName}-DocCodingStatusInput-${idx}`}
                  >
                    <input id={`${componentName}-DocCodingStatusInput-${idx}`} className="form-check-input me-2" type="checkbox" value="" checked={false} readOnly />
                    <div className="nowrap">{e}</div>
                  </label>
                ))}
              </div>
            )}
            {name === "Needed Referrals" && (
              <div id={`${componentName}-NeedReferralsStatus-${idx}`} className="d-flex flex-wrap w-100">
                {["Ordered", "Marked for Closure", "Patient Refused", "Excluded", "Previously Completed"].map((e, idx) => (
                  <label
                    key={`${componentName}-NeedReferralsStatusCheck-${idx}`}
                    id={`${componentName}-NeedReferralsStatusCheck-${idx}`}
                    className="d-flex form-check-label me-3"
                    htmlFor={`${componentName}-NeedReferralsStatusInput-${idx}`}
                  >
                    <input id={`${componentName}-NeedReferralsStatusInput-${idx}`} className="form-check-input me-2" type="checkbox" value="" checked={false} readOnly />
                    <div className="nowrap">{e}</div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ActivityWrapper>
  ));
};

const RecommendedActivities = ({ patient, extraData }) => {
  const [mvDtos, setMvDtos] = useState([]);
  const headers = {
    docandcod: <></>,
    treatandplan: <></>,
    others: <></>,
  };

  useEffect(() => {
    const sortList = ["preventionandscreenings", "documentationandcoding", "medicationreview", "neededreferrals"];
    if (patient?.manageVisitActivityTypeDtos?.length > 0) {
      let mvActivityTypeDtos = cloneDeep(patient.manageVisitActivityTypeDtos);
      const findIndex = mvActivityTypeDtos.findIndex((a) => a.name === "Treatment Planning");
      if (findIndex !== -1) {
        mvActivityTypeDtos.splice(findIndex, 1);
      }

      if (hasSDOH(mvActivityTypeDtos) && !hasGapType(mvActivityTypeDtos, "Needed Referrals")) {
        mvActivityTypeDtos.push({
          name: "Needed Referrals",
          visitActivitySubTypeDtos: [{ visitActivities: [{ disabled: false }] }],
        });
        mvActivityTypeDtos = mvActivityTypeDtos.sort((a, b) => {
          return (
            sortList.indexOf(a.name.replaceAll(" ", "").toLowerCase()) -
            sortList.indexOf(b.name.replaceAll(" ", "").toLowerCase())
          );
        });
        setMvDtos(mvActivityTypeDtos);
      } else {
        mvActivityTypeDtos = mvActivityTypeDtos.sort((a, b) => {
          return (
            sortList.indexOf(a.name.replaceAll(" ", "").toLowerCase()) -
            sortList.indexOf(b.name.replaceAll(" ", "").toLowerCase())
          );
        });
        setMvDtos(mvActivityTypeDtos);
      }
    }
    // Recompute recommended activities when the patient activity payload changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, patient.manageVisitActivityTypeDtos]);

  const hasSDOH = useCallback(
    (manageVisitActivityTypeDtos) => {
      return manageVisitActivityTypeDtos
        ?.flatMap((n) => n.visitActivitySubTypeDtos)
        ?.flatMap((x) => x.visitActivities)
        .some((g) => g.abbreviation);
    },
    []
  );

  const hasGapType = useCallback((manageVisitActivityTypeDtos, gap) => {
    return manageVisitActivityTypeDtos?.some((n) => n.name === gap);
  }, []);
  return (
    <>
      {mvDtos?.map((e, idx) => {
        const activitiesFiltered = e?.visitActivitySubTypeDtos
          .flat()
          .filter((f) => f?.visitActivities.some((g) => g?.disabled === false));

        const name = {
          TreatmentPlanning: headers.treatandplan,
          DocumentationAndCoding: headers.docandcod,
          others: headers.others,
        };

        return (
          <div
            key={`${componentName}-manageVisitActivity-${e?.name}-${idx}`}
            id={`${componentName}-manageVisitActivity-${idx}`}
          >
            {activitiesFiltered.length > 0 && (
              <div className="border-bottom mb-3 pb-3">
                {e?.name !== "Documentation And Coding" && (
                  <h5 id={`${componentName}-header-${idx}`} className="fs-5 fw-bold mb-1">
                    {e?.name}
                  </h5>
                )}
                {e?.name === "Documentation And Coding" && (
                  <h5 id={`${componentName}-header-${idx}`} className="fs-5 fw-bold mb-1">
                    Documentation and Coding
                  </h5>
                )}
                {e?.name.replaceAll(" ", "") in name ? name[e?.name.replaceAll(" ", "")] : name.others}
                <VisitActivitySubType
                  name={e?.name}
                  extraData={extraData}
                  subTypes={e?.visitActivitySubTypeDtos}
                  visitActivityType={e}
                  missedOpportunities={patient.missedOpportunities}
                />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

RecommendedActivities.propTypes = {
  patient: PropTypes.object,
  extraData: PropTypes.object,
};

export default RecommendedActivities;
